import { Clock, ShieldAlert } from 'lucide-react'
import type { ProfileResponse, TrustStatusResponse } from '../../types/api'

/**
 * Where an unverified provider stands, said plainly. Verification no longer gates the map or
 * connecting - it earns the Verified badge - so this tells them what the badge is waiting on.
 */
export function VerificationGateBanner({
  profile,
  status,
}: {
  profile: ProfileResponse
  status: TrustStatusResponse | null
}) {
  if (profile.persona !== 'SUPPLY' || profile.verificationStatus === 'VERIFIED') {
    return null
  }

  const awaitingReview = profile.verificationStatus === 'DOCS_DECLARED'
  const suspended = profile.verificationStatus === 'SUSPENDED'
  const note = status?.verificationNote

  return (
    <div
      className={`mb-6 rounded-xl border px-4 py-3 ${
        suspended
          ? 'border-red-300 bg-red-50'
          : awaitingReview
            ? 'border-sun-400/60 bg-sun-100'
            : 'border-amber-300 bg-amber-50'
      }`}
    >
      <div className="flex items-start gap-2">
        {awaitingReview ? (
          <Clock size={17} className="mt-0.5 shrink-0 text-amber-700" />
        ) : (
          <ShieldAlert size={17} className="mt-0.5 shrink-0 text-red-600" />
        )}
        <div className="min-w-0">
          <p className="font-semibold text-ink-900">
            {suspended
              ? 'Your account is suspended'
              : awaitingReview
                ? 'Awaiting verification'
                : 'Not verified yet'}
          </p>
          <p className="mt-0.5 text-sm text-ink-700">
            You are on the map and can connect with learners already. Once a YellowDots team
            member verifies you, a <strong>Verified</strong> badge appears on your listing.
          </p>
          {!awaitingReview && !suspended && (
            <p className="mt-1 text-sm text-ink-700">
              The YellowDots team will get in touch to verify you.
            </p>
          )}
          {note && (
            <p className="mt-2 rounded-lg bg-white/70 px-2.5 py-1.5 text-sm text-ink-800">
              <span className="font-semibold">Note from the team:</span> {note}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
